import { GetStaticProps } from "next"
import { useContext } from "react"
import { Box, Container, Grid, Paper, Hidden } from "@mui/material"
import { Trans } from "next-i18next"

import AppContext from "@secureo/common/context/AppContext/AppContext"
import BreadCrumbs, { BreadCrumbAncestor } from "components/BreadCrumbs"
import Price from "components/Price"

import useTranslation from "hooks/useTranslation"

import { getServerTranslations } from "utils/i18n/getServerTranslations"
import { makeStyles } from "makeStyles"
import { helpCenterBreadCrumb } from "./index"
import { flatFeesByCurrencyCode } from "@secureo/common/config/shippingZonesAndCosts"

const useStyles = makeStyles()((theme) => ({
	paper: {
		padding: theme.spacing(4),
		color: "black",
		boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.15)",
		minHeight: "200px",
	},
	firstPaper: {
		padding: theme.spacing(4),
		color: "black",
		boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.15)",
		minHeight: "100px",
		marginTop: "80px",
	},
}))

const WhyChooseUs = () => {
	const { classes } = useStyles()
	const { currencyCode } = useContext(AppContext)

	const { t } = useTranslation("help")

	const breadCrumb: BreadCrumbAncestor = {
		label: t("whyWithUs"),
		href: "/help/why-choose-us",
	}

	return (
		<Box my={5}>
			<Box style={{ marginTop: "-40px", paddingBottom: "20px" }}>
				<BreadCrumbs
					breadCrumbsPaths={[
						{ ...helpCenterBreadCrumb, label: t(helpCenterBreadCrumb.label) },
					]}
					currentPath={breadCrumb}
				/>
			</Box>
			<Container>
				<Box pb={5} style={{ textAlign: "center", maxWidth: "650px", margin: "auto" }}>
					<h1 style={{ marginBottom: "0" }}>{t("whyWithUs")}</h1>
					<h2 style={{ marginTop: "0" }}>{t("reasonsForUs")}</h2>
					<p style={{ color: "grey" }}>{t("reasonsForUsText")}</p>
				</Box>
			</Container>
			<Box
				my={5}
				style={{
					background:
						"url(https://res.cloudinary.com/safehero/image/upload/v1572507497/HelpCenter/wl1fiq5l9wwxpeoxypqo.png)",
					width: "100%",
					objectFit: "contain",
					backgroundRepeat: "no-repeat",
					minHeight: "380px",
				}}
			>
				<Container>
					<Grid container spacing={3}>
						<Grid item xs={12} md={5}></Grid>
						<Grid item xs={12} md={7}>
							<Box my={5}>
								<Paper className={classes.firstPaper}>
									<div
										style={{
											borderBottom: "1px solid lightgrey",
											paddingBottom: "5px",
										}}
										className="h2"
									>
										<span style={{ borderBottom: "6px solid #7abfac" }}>
											{t("freeDelivery")}
										</span>
									</div>

									<p>
										<Trans
											t={t}
											i18nKey="freeDeliveryFrom"
											components={{
												Fee: (
													<Price
														priceInEuroCents={
															flatFeesByCurrencyCode[currencyCode]
																.flatFeeForSmallAmountThresholdGrossCents
														}
													/>
												),
											}}
										/>
									</p>
								</Paper>
							</Box>
						</Grid>
					</Grid>
				</Container>
			</Box>
			<Container>
				<Box mt={5} py={5}>
					<Grid container spacing={3}>
						<Grid item xs={12} md={6}>
							<div style={{ margin: "auto", textAlign: "center" }}>
								<img
									alt="gratis"
									style={{ maxWidth: "350px", maxHeight: "300px" }}
									src="https://res.cloudinary.com/safehero/image/upload/v1572507457/HelpCenter/ty6agenhddwbu1y7dozo.png"
								/>
							</div>
						</Grid>
						<Grid item xs={12} md={6}>
							<div
								style={{
									borderBottom: "1px solid lightgrey",
									paddingBottom: "5px",
								}}
								className="h2"
							>
								<span style={{ borderBottom: "6px solid #7abfac" }}>
									{t("largeSelection")}
								</span>
							</div>
							<p style={{ color: "grey" }}>{t("largeSelectionText1")}</p>
							<p>{t("largeSelectionText2")}</p>
						</Grid>
					</Grid>
				</Box>
			</Container>
			<Box
				my={5}
				style={{
					background:
						"url(https://res.cloudinary.com/safehero/image/upload/v1571664491/HelpCenter/Background_uw7rek.png)",
					width: "100%",
					objectFit: "contain",
					minHeight: "400px",
				}}
			>
				<Container>
					<Grid container spacing={3}>
						<Grid item xs={12} md={7}>
							<Box my={5}>
								<Paper className={classes.firstPaper}>
									<div
										style={{
											borderBottom: "1px solid lightgrey",
											paddingBottom: "5px",
										}}
										className="h2"
									>
										<span style={{ borderBottom: "6px solid #7abfac" }}>
											{t("greatCustomerService")}
										</span>
									</div>
									<p>{t("greatCustomerServiceText")}</p>
								</Paper>
							</Box>
						</Grid>
						<Grid item md={5}>
							<Hidden mdDown>
								<div style={{ color: "#f7fafa", paddingTop: "150px" }}>
									<strong style={{ fontSize: "25px" }}>
										Andreas K., Gönnheim
									</strong>
									<p>„{t("customerSupportReview")}“</p>
								</div>
							</Hidden>
						</Grid>
					</Grid>
				</Container>
			</Box>
			<Box py={5}>
				<Container>
					<Grid container spacing={3}>
						<Grid item xs={12} md={6}>
							<div style={{ margin: "auto", textAlign: "center" }}>
								<img
									alt="gratis"
									style={{ maxWidth: "350px", maxHeight: "200px" }}
									src="https://res.cloudinary.com/safehero/image/upload/v1572507457/HelpCenter/yrvbxn0pt7d1cqhfohqx.png"
								/>
							</div>
						</Grid>
						<Grid item xs={12} md={6}>
							<div
								style={{
									borderBottom: "1px solid lightgrey",
									paddingBottom: "5px",
								}}
								className="h2"
							>
								<span style={{ borderBottom: "6px solid #7abfac" }}>
									{t("bestOffers")}
								</span>
							</div>
							<p>{t("bestOffersText")}</p>
						</Grid>
					</Grid>
				</Container>
			</Box>
			<Box
				mt={5}
				style={{
					background:
						"url(https://res.cloudinary.com/safehero/image/upload/v1571664483/HelpCenter/Background2_dlzxzt.png)",
					width: "100%",
					objectFit: "contain",
					backgroundRepeat: "no-repeat",
					minHeight: "30px",
				}}
			>
				<Container>
					<Grid container spacing={3}>
						<Grid item xs={12} md={5}></Grid>
						<Grid item xs={12} md={7}>
							<Box my={5}>
								<Paper className={classes.firstPaper}>
									<div
										style={{
											borderBottom: "1px solid lightgrey",
											paddingBottom: "5px",
										}}
										className="h2"
									>
										<span style={{ borderBottom: "6px solid #7abfac" }}>
											{t("safeShopping")}
										</span>
									</div>

									<p>{t("safeShoppingText")}</p>
								</Paper>
							</Box>
						</Grid>
					</Grid>
				</Container>
			</Box>
		</Box>
	)
}

export const getStaticProps: GetStaticProps = async (context) => {
	const props = {
		...(await getServerTranslations(context.locale, [
			"common",
			"countries",
			"languages",
			"help",
		])),
	}

	return {
		props,
	}
}

export default WhyChooseUs
